/* ============================================================
   ilo sitelen — the sentence machine
   ------------------------------------------------------------
   Turns parsed toki pona sentences into drawable SceneSpecs, and
   (in reverse) turns random SceneSpecs into target scenes for the
   learner to describe.

   The parser is reused from tokiPonaCompose (`parseSentence`).
   This module never touches React or SVG — it only maps parse
   trees to a small normalized scene vocabulary, so all grading
   logic is testable in plain Jest.

   Design decisions:
   - Ambiguity-friendly grading: a description is correct if ANY
     parser reading of it maps to the target scene, not just the
     top one. toki pona is ambiguous on purpose; the machine
     shouldn't punish that.
   - Quality folding: "kili li loje", "kili loje" and a red-fruit
     drawing are all the same scene. Copular predicates whose head
     is an attribute word FOLD into the subject's attributes, so
     every grammatical way of saying "the fruit is red" collides.
   - Strict, not lenient, on unmappable words: a modifier or verb
     the machine can't draw makes that reading unrenderable
     (with a reason code for UI hints) rather than being silently
     dropped — otherwise reverse mode would accept word salad.

   Shapes:
   - Ent:   { e, color, size, count, mood, neg }
   - Scene: { subject:Ent, verb, verbNeg, object:Ent|null,
              place:Ent|null, dest:Ent|null, question,
              imperative, emphasis }
   ============================================================ */

import { parseSentence } from "./tokiPonaCompose";

/* ---------------- supported vocabulary ---------------- */

// Words the machine can draw as a thing (subject / object / dest).
// Pronouns render as person variants and take no modifiers.
export const PRONOUN_ENTITIES = ["mi", "sina", "ona"];
export const ENTITIES = [
  // creatures
  "jan", "soweli", "waso", "kala", "akesi", "pipi",
  // food & nature
  "kili", "pan", "kasi", "telo", "suno", "mun", "ma", "seli", "kiwen",
  // things & structures
  "tomo", "poki", "lipu", "ilo", "supa", "len", "mani", "palisa", "sike",
];
const ENTITY_SET = new Set([...ENTITIES, ...PRONOUN_ENTITIES]);
const PRONOUN_SET = new Set(PRONOUN_ENTITIES);

// Creatures can be scene subjects in reverse mode (anything can be a
// subject in forward mode — a sleeping bread is legal and encouraged).
export const CREATURES = ["jan", "soweli", "waso", "kala", "akesi", "pipi"];

// Verbs the machine can animate. Value = animation id for the renderer.
export const ACTIONS = {
  moku: "chomp",
  tawa: "move",
  lape: "sleep",
  lukin: "gaze",
  toki: "speak",
  pali: "work",
  musi: "wiggle",
  utala: "clash",
  pana: "offer",
  jo: "hold",
  kute: "listen",
  olin: "hearts",
  mu: "sound",
  kalama: "sound",
  sitelen: "draw",
};

// Attribute words → Ent fields.
export const COLOR_MODS = {
  loje: "#d95d43",
  jelo: "#e5b93c",
  laso: "#4a9db4",
  walo: "#efece2",
  pimeja: "#33323b",
};
export const SIZE_MODS = { suli: 1.9, lili: 0.42 };
export const COUNT_MODS = { wan: 1, tu: 2, mute: 3 };
export const MOOD_MODS = ["pona", "ike", "nasa", "wawa", "suwi"];
const MOOD_SET = new Set(MOOD_MODS);

// Heads allowed after "lon" (backdrop places).
export const PLACES = ["tomo", "ma", "telo", "kasi", "supa"];
const PLACE_SET = new Set(PLACES);

// Everything the page's completions pool should offer.
export const SUPPORTED_WORDS = [
  ...new Set([
    ...ENTITIES, ...PRONOUN_ENTITIES, ...Object.keys(ACTIONS),
    ...Object.keys(COLOR_MODS), ...Object.keys(SIZE_MODS),
    ...Object.keys(COUNT_MODS), ...MOOD_MODS,
    "li", "e", "lon", "o", "ala", "a",
  ]),
];

/* ---------------- entity mapping ---------------- */

const blankEnt = (e) => ({ e, color: null, size: null, count: 1, mood: null, neg: false });

// Apply one attribute word to an Ent. Returns null on success or a
// reason object on failure. `intensifiers` (copular position) lets
// mute/lili act as "very / slightly" and vanish instead of counting.
function applyAttr(ent, word, intensifiers = false) {
  if (COLOR_MODS[word]) {
    if (ent.color && ent.color !== word) return { code: "two-colors", word };
    ent.color = word;
  } else if (SIZE_MODS[word]) {
    ent.size = word;
  } else if (COUNT_MODS[word] !== undefined) {
    if (intensifiers && word !== "wan") return null; // "suli mute" = very big
    ent.count = COUNT_MODS[word];
  } else if (MOOD_SET.has(word)) {
    ent.mood = word;
  } else {
    return { code: "mod", word };
  }
  return null;
}

// NP → Ent. Returns { entity } or { reason: {code, word?} }.
export function npToEntity(np) {
  if (!np || !ENTITY_SET.has(np.head)) {
    return { reason: { code: "entity", word: np && np.head } };
  }
  if (PRONOUN_SET.has(np.head) && np.items.length) {
    return { reason: { code: "pronoun-mods", word: np.head } };
  }
  const ent = blankEnt(np.head);
  for (const it of np.items) {
    if (it.kind === "pi") return { reason: { code: "pi" } };
    if (it.kind === "neg") { ent.neg = true; continue; }
    const bad = applyAttr(ent, it.word);
    if (bad) return { reason: bad };
  }
  return { entity: ent };
}

/* ---------------- tree → scene ---------------- */

const blankScene = () => ({
  subject: null, verb: null, verbNeg: false, object: null,
  place: null, dest: null, question: false, imperative: false, emphasis: false,
});

// One prep list → { place } / { dest-less ok } or { reason }.
// Only a single "lon <place>" phrase is drawable.
function mapPreps(preps, scene) {
  if (!preps.length) return null;
  if (preps.length > 1) return { code: "many-preps" };
  const p = preps[0];
  if (p.word !== "lon") return { code: "prep", word: p.word };
  const r = npToEntity(p.np);
  if (r.reason) return r.reason;
  if (!PLACE_SET.has(r.entity.e)) return { code: "place", word: r.entity.e };
  scene.place = r.entity;
  return null;
}

// Predicate alt (+ mapped subject Ent) → fills `scene` or returns a reason.
function mapPred(pred, subject, scene) {
  if (pred.kind === "anu") return { code: "anu" };
  if (pred.preverbs && pred.preverbs.length) return { code: "preverb" };
  scene.subject = subject;
  scene.question = scene.question || !!pred.ynq;

  if (pred.kind === "prepHead") {
    if (pred.head === "lon") {
      const r = npToEntity(pred.np);
      if (r.reason) return r.reason;
      if (!PLACE_SET.has(r.entity.e)) return { code: "place", word: r.entity.e };
      scene.place = r.entity;
      return null;
    }
    if (pred.head === "tawa") {
      const r = npToEntity(pred.np);
      if (r.reason) return r.reason;
      scene.verb = "tawa";
      scene.dest = r.entity;
      return null;
    }
    return { code: "prep", word: pred.head };
  }

  if (pred.kind === "become") return { code: "become" };

  if (pred.kind === "verbal") {
    if (!ACTIONS[pred.head]) return { code: "verb", word: pred.head };
    scene.verb = pred.head;
    const items = [...pred.np.items];
    if (items[0] && items[0].kind === "neg") {
      scene.verbNeg = true;
      items.shift();
    }
    if (items.length) return { code: "manner", word: items[0].word };
    if (pred.objects.length > 1) return { code: "objects" };
    if (pred.objects.length) {
      const r = npToEntity(pred.objects[0]);
      if (r.reason) return r.reason;
      scene.object = r.entity;
    }
    return mapPreps(pred.preps, scene);
  }

  // copular — fold attribute head + mods into the subject
  if (ENTITY_SET.has(pred.np.head)) return { code: "copular-entity", word: pred.np.head };
  const folded = { ...subject };
  let bad = applyAttr(folded, pred.np.head);
  if (bad) return bad;
  for (const it of pred.np.items) {
    if (it.kind === "pi") return { code: "pi" };
    if (it.kind === "neg") return { code: "neg-quality" };
    bad = applyAttr(folded, it.word, true);
    if (bad) return bad;
  }
  scene.subject = folded;
  return mapPreps(pred.preps, scene);
}

// Parse-tree → Scene. Returns { scene } or { reason }.
export function treeToScene(tree) {
  const scene = blankScene();
  if (!tree) return { reason: { code: "empty" } };

  if (tree.type === "fragment") {
    const r = npToEntity(tree.np);
    if (r.reason) return r;
    scene.subject = r.entity;
    return { scene };
  }

  if (tree.type === "imperative") {
    scene.imperative = true;
    let subject;
    if (tree.vocative) {
      const v = npToEntity(tree.vocative);
      if (v.reason) return v;
      subject = v.entity;
    } else {
      subject = blankEnt("sina"); // "o moku!" — the machine draws you
    }
    if (tree.predicates.length !== 1) return { reason: { code: "multi-pred" } };
    const bad = mapPred(tree.predicates[0], subject, scene);
    return bad ? { reason: bad } : { scene };
  }

  if (tree.type === "clause") {
    if (tree.subjects.length !== 1) return { reason: { code: "multi-subject" } };
    if (tree.predicates.length !== 1) return { reason: { code: "multi-pred" } };
    const s = npToEntity(tree.subjects[0]);
    if (s.reason) return s;
    const bad = mapPred(tree.predicates[0], s.entity, scene);
    return bad ? { reason: bad } : { scene };
  }

  return { reason: { code: "type", word: tree.type } };
}

/* ---------------- text → scene (public) ---------------- */

// Every renderable scene across all readings, best-scored first.
// Result: { scenes:[{scene, reading}], reasons:[...], errors:[...] }
export function renderableScenes(text) {
  const result = parseSentence(text);
  const scenes = [];
  const reasons = [];
  result.readings.forEach((reading) => {
    const r = treeToScene(reading.tree);
    if (r.scene) {
      r.scene.emphasis = result.emphasis;
      scenes.push({ scene: r.scene, reading });
    } else if (!reasons.some((x) => x.code === r.reason.code && x.word === r.reason.word)) {
      reasons.push(r.reason);
    }
  });
  return { scenes, reasons, errors: result.errors };
}

// Forward mode: the single scene to draw (top renderable reading).
export function renderScene(text) {
  const { scenes, reasons, errors } = renderableScenes(text);
  return {
    scene: scenes.length ? scenes[0].scene : null,
    reading: scenes.length ? scenes[0].reading : null,
    reasons,
    errors,
  };
}

/* ---------------- scene equality ---------------- */

const entKey = (e) =>
  e ? [e.e, e.color, e.size, e.count, e.mood, e.neg].join("|") : "-";

export function sceneKey(s) {
  return [
    entKey(s.subject), s.verb || "-", s.verbNeg ? "!" : "",
    entKey(s.object), entKey(s.place), entKey(s.dest),
    s.question ? "?" : "", s.imperative ? "o" : "",
  ].join("//"); // emphasis is decoration, never graded
}

export const scenesMatch = (a, b) => !!a && !!b && sceneKey(a) === sceneKey(b);

// Reverse mode grading: does ANY reading of `text` draw the target?
export function matchDescription(text, target) {
  const { scenes, reasons, errors } = renderableScenes(text);
  const hit = scenes.find(({ scene }) => scenesMatch(scene, target));
  return {
    ok: !!hit,
    scene: hit ? hit.scene : scenes.length ? scenes[0].scene : null,
    reasons,
    errors,
  };
}

/* ---------------- scene → sentence ---------------- */

// Canonical toki pona for an Ent (head + color + size + mood + count).
function entTokens(e) {
  const t = [e.e];
  if (e.color) t.push(e.color);
  if (e.size) t.push(e.size);
  if (e.mood) t.push(e.mood);
  if (e.count === 2) t.push("tu");
  else if (e.count === 3) t.push("mute");
  if (e.neg) t.push("ala");
  return t;
}

// Canonical sentence for a scene — used for reverse-mode reveals and
// the roundtrip tests. Not the ONLY correct answer, just one of them.
export function specToSentence(scene) {
  const subj = entTokens(scene.subject);
  const barePronoun =
    PRONOUN_SET.has(scene.subject.e) && subj.length === 1 && scene.subject.e !== "ona";
  const pred = [];
  if (scene.verb === "tawa" && scene.dest) {
    pred.push("tawa", ...entTokens(scene.dest));
  } else if (scene.verb) {
    pred.push(scene.verb);
    if (scene.verbNeg) pred.push("ala");
    if (scene.object) pred.push("e", ...entTokens(scene.object));
  }
  if (scene.place) pred.push("lon", ...entTokens(scene.place));
  if (!pred.length) return subj.join(" "); // attribute-only scene → fragment
  if (scene.imperative) return [...subj.filter(() => scene.subject.e !== "sina" || subj.length > 1), "o", ...pred].join(" ").trim();
  return [...subj, ...(barePronoun ? [] : ["li"]), ...pred].join(" ");
}

// A couple of alternate valid phrasings for the reveal panel.
export function alternatePhrasings(scene) {
  const alts = [];
  const canonical = specToSentence(scene);
  // attribute-only scenes can also be said with li + quality
  if (!scene.verb && !scene.place && scene.subject) {
    const s = scene.subject;
    const attrs = [s.color, s.size, s.mood].filter(Boolean);
    if (attrs.length === 1 && !PRONOUN_SET.has(s.e)) {
      const bare = entTokens({ ...s, color: null, size: null, mood: null });
      alts.push([...bare, "li", attrs[0]].join(" "));
    }
  }
  return alts.filter((a) => a !== canonical);
}

/* ---------------- reverse mode: scene generation ---------------- */

// Deterministic RNG for tests; the page can pass Math.random.
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const INTRANS = ["lape", "musi", "toki", "moku", "tawa", "mu", "pali", "utala", "sitelen", "kalama"];
const TRANS = {
  moku: ["kili", "pan", "telo"],
  lukin: ["soweli", "waso", "kala", "kili", "tomo", "lipu", "suno", "mun"],
  jo: ["kili", "pan", "poki", "lipu", "ilo", "mani", "palisa", "len"],
  olin: ["jan", "soweli", "waso", "kala", "kili", "mun"],
  utala: ["jan", "soweli", "akesi", "pipi"],
  pana: ["kili", "pan", "poki", "mani", "lipu"],
  kute: ["jan", "waso", "soweli", "telo"],
};
const ATTR_KINDS = ["color", "size", "count", "mood"];

const pick = (rand, arr) => arr[Math.floor(rand() * arr.length)];

function randomAttr(rand, ent) {
  const kind = pick(rand, ATTR_KINDS);
  if (kind === "color") ent.color = pick(rand, Object.keys(COLOR_MODS));
  else if (kind === "size") ent.size = pick(rand, Object.keys(SIZE_MODS));
  else if (kind === "count") ent.count = pick(rand, [2, 3]);
  else ent.mood = pick(rand, MOOD_MODS);
}

// level 1: thing + attribute · level 2: someone does something ·
// level 3: transitive + object · level 4: level 2/3 plus a place.
export function randomScene(level = 1, rand = Math.random) {
  const scene = blankScene();
  if (level <= 1) {
    scene.subject = blankEnt(pick(rand, ENTITIES));
    randomAttr(rand, scene.subject);
    return scene;
  }
  scene.subject = blankEnt(pick(rand, CREATURES));
  if (rand() < 0.5) randomAttr(rand, scene.subject);
  if (level === 2) {
    scene.verb = pick(rand, INTRANS);
    return scene;
  }
  const verb = pick(rand, Object.keys(TRANS));
  scene.verb = verb;
  scene.object = blankEnt(pick(rand, TRANS[verb]));
  if (rand() < 0.4) randomAttr(rand, scene.object);
  if (level >= 4) {
    scene.place = blankEnt(pick(rand, PLACES));
    if (rand() < 0.3) scene.place.color = pick(rand, Object.keys(COLOR_MODS));
  }
  return scene;
}
